import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';


import { BaseService } from '../base/base.service';
import { Item } from '../../models/lookups/item';
import { ItemItemType } from '../../models/lookups/item-item-type';
import { ItemOperation } from '../../models/lookups/ItemOperation';
import { Query } from '../../models/query/query';
import { WebMethodResult } from '../../models/web-method-result';
import { AppModulePath } from '../../models/app-module-path';
import { ItemTool } from 'app/models/lookups/item-tool';
import { HttpClient } from '@angular/common/http';
import { PlatformLocation } from '@angular/common';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class ItemService extends BaseService {
  private listUrl = '/api/items/list';
  private listForOutUrl = '/api/items/listForOut';
  private listForOutPlusServicesUrl = '/api/items/listForOutPlusServices';
  private saveUrl = '/api/items/save';
  private findItemByIdUrl = '/api/items/itemById';
  private deleteUrl = '/api/items/delete/';
  private importUrl = '/api/items/import';
  private listServicesUrl = '/api/item-item-type/listServices';
  private listItemItemTypeByItemIdUrl = '/api/item-item-type/byItemId';
  private countUrl = '/api/items/count';
  private updateUrl = '/api/items/update';
  private listIOUrl = '/api/itop/list';
  private saveIOUrl = '/api/itop/save';
  private deleteIOUrl = '/api/itop/delete';
  private updateIOUrl = '/api/itop/update';
  private sendImage = '/api/items/sendImage';
  private getImagesUrl = '/api/items/getImages';
  private autogenerateCodesUrl = '/api/items/checkIfAutogeneratingCodes';
  private getNextItemCodeUrl = '/api/items/getNextItemCode';
  private listWorkOrderItemsUrl = '/api/items/list-work-order-items';
  private listItemsByStockroom = '/api/items/itemsByStockroom';
  private listItemsSredstvaUrl = '/api/items/list-items-sredstva';

  private listITUrl = '/api/item-tool/list';
  private saveITUrl = '/api/item-tool/save';
  private deleteITUrl = '/api/item-tool/delete';
  private updateITUrl = '/api/item-tool/update';

  private listProizvodiPoluproizvodi = '/api/items/list-proizvod-poluproizvod';
  private updateSortsUrl = '/api/itop/update-sorts';

  constructor(protected injector: Injector,
    protected http2: HttpClient,
    protected platformLocation: PlatformLocation) {
    super(injector, http2, platformLocation);
  }

  list(qry: Query): Observable<Item[]> {
    const url = this.listUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<Item[]>(url, AppModulePath.ITEM).pipe(
      map(res => res.map(obj => new Item(obj))),
      catchError(this.handleError));
  }

  listByStockroom(itemId: String, qry: Query): Observable<Item[]> {
    const url = this.listItemsByStockroom + '/' + itemId + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<Item[]>(url, AppModulePath.ITEM).pipe(
      map(res => res.map(obj => new Item(obj))),
      catchError(this.handleError));
  }

  listIO(qry: Query): Observable<ItemOperation[]> {
    const url = this.listIOUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<ItemOperation[]>(url, AppModulePath.ITEM_OPERATION).pipe(
      map(res => res.map(obj => new ItemOperation(obj))),
      catchError(this.handleError));
  }

  listIT(qry: Query): Observable<ItemTool[]> {
    const url = this.listITUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<ItemTool[]>(url, AppModulePath.ITEM_TOOL).pipe(
      map(res => res.map(obj => new ItemTool(obj))),
      catchError(this.handleError));
  }

  checkIfAutogeneratingCodes(): Observable<any> {
    return this.get(this.autogenerateCodesUrl, AppModulePath.ITEM).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  getNextItemCode(id: number): Observable<any> {
    return this.get(this.getNextItemCodeUrl + '/' + id, AppModulePath.ITEM).pipe(
      map(res => res),
      catchError(this.handleError));
  }


  save_IO(item: ItemOperation): Observable<ItemOperation> {
    return this.post<ItemOperation>(this.saveIOUrl, AppModulePath.ITEM_OPERATION, item).pipe(
      map(res => new ItemOperation(res)),
      catchError(this.handleError));
  }

  save_IT(item: ItemTool): Observable<ItemTool> {
    return this.post<ItemTool>(this.saveITUrl, AppModulePath.ITEM_TOOL, item).pipe(
      map(res => new ItemTool(res)),
      catchError(this.handleError));
  }

  delete_IO(itemId: String, operId: number): Observable<WebMethodResult> {
    return this._delete<WebMethodResult>(this.deleteIOUrl + '/' + itemId + '/' + operId, AppModulePath.ITEM_OPERATION).pipe(
      map(res => new WebMethodResult(res)),
      catchError(this.handleError));
  }

  delete_IT(itemId: String, toolId: number): Observable<WebMethodResult> {
    return this._delete<WebMethodResult>(this.deleteITUrl + '/' + itemId + '/' + toolId, AppModulePath.ITEM_TOOL).pipe(
      map(res => new WebMethodResult(res)),
      catchError(this.handleError));
  }

  update_IO(item: ItemOperation) {
    return this.post(this.updateIOUrl, AppModulePath.ITEM_OPERATION, item)
      .subscribe(res => res);
  }

  update_IT(item: ItemTool) {
    return this.post(this.updateITUrl, AppModulePath.ITEM_TOOL, item)
      .subscribe(res => res);
  }

  count(qry: Query): Observable<number> {
    const url = this.countUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<number>(url, AppModulePath.ITEM).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  listForOut(qry: Query): Observable<Item[]> {
    const url = this.listForOutUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<Item[]>(url, AppModulePath.ITEM).pipe(
      map(res => res.map(obj => new Item(obj))),
      catchError(this.handleError));
  }

  listForOutPlusServices(qry: Query): Observable<Item[]> {
    const url = this.listForOutPlusServicesUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<Item[]>(url, AppModulePath.ITEM).pipe(
      map(res => res.map(obj => new Item(obj))),
      catchError(this.handleError));
  }

  listServices(qry: Query): Observable<ItemItemType[]> {
    const url = this.listServicesUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<ItemItemType[]>(url, AppModulePath.PRODUCT).pipe(
      map(res => res.map(obj => new ItemItemType(obj))),
      catchError(this.handleError));
  }

  listItemItemTypeByItemId(itemId: string): Observable<ItemItemType[]> {
    return this.get<ItemItemType[]>(this.listItemItemTypeByItemIdUrl + '/' + itemId, AppModulePath.PRODUCT).pipe(
      map(res => res.map(obj => new ItemItemType(obj))),
      catchError(this.handleError));
  }

  findItemById(id: string): Observable<Item> {
    return this.get<Item>(this.findItemByIdUrl + '/' + id, AppModulePath.ITEM).pipe(
      map(res => new Item(res)),
      catchError(this.handleError));
  }

  save(item: Item): Observable<Item> {
    return this.post<Item>(this.saveUrl, AppModulePath.ITEM, item).pipe(
      map(res => new Item(res)),
      catchError(this.handleError));
  }

  delete(id: number): Observable<WebMethodResult> {
    return this._delete<WebMethodResult>(this.deleteUrl + id, AppModulePath.ITEM).pipe(
      map(res => new WebMethodResult(res)),
      catchError(this.handleError));
  }


  uploadImportFile(formData: FormData): Observable<Blob> {
    return this.uploadFile(this.importUrl, AppModulePath.ITEM, formData).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  update(item: Item) {
    return this.post(this.updateUrl, AppModulePath.ITEM, item)
      .subscribe(res => res);
  }

  export(reportName: string, query: Query): any {
    return this.downloadFile('/api/itop/report/' + reportName, AppModulePath.ITEM, query).pipe(
      map((res) => {
        // console.log(res);
        return new Blob([res], { type: 'application/pdf' });
      }),
      catchError(this.handleError));
  }

  getImagesNumber(id: any): Observable<number> {
    return this.get<number>(this.getImagesUrl + '/' + id, AppModulePath.ITEM).pipe(
      map(res => res),
      catchError(this.handleError));
  }


  uploadImage(formData: FormData): Observable<any> {
    return this.uploadFile(this.sendImage, AppModulePath.ITEM, formData).pipe(
      map(res => res),
      catchError(this.handleError));
  }

  listWorkOrderItems(qry: Query): Observable<Item[]> {
    const url = this.listWorkOrderItemsUrl + '?q=' + encodeURIComponent(JSON.stringify(qry));
    return this.get<Item[]>(url, AppModulePath.ITEM).pipe(
      map(res => res.map(obj => new Item(obj))),
      catchError(this.handleError));
  }

  listItemsSredstva(): Observable<Item[]> {
    return this.get<Item[]>(this.listItemsSredstvaUrl, AppModulePath.OSNOVNA_SREDSTVA).pipe(
      map(res => res.map(obj => new Item(obj))),
      catchError(this.handleError));
  }
  listProizvodPoluproizvod(groupId?: number): Observable<Item[]> {
    const params: any = {};
    if (groupId !== undefined && groupId !== null) {
      params.groupId = groupId;
    }
    return this.get<Item[]>(this.listProizvodiPoluproizvodi, AppModulePath.ITEM, { params }).pipe(
      map(res => res.map(obj => new Item(obj))),
      catchError(this.handleError));
  }

  updateSorts(item: ItemOperation[]): Observable<WebMethodResult> {
    return this.put<WebMethodResult>(this.updateSortsUrl, AppModulePath.ITEM_OPERATION, item).pipe(
      map(res => new WebMethodResult(res)),
      catchError(this.handleError)
    );
  }
}
